import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Alert,
  DialogContentText,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";

import { DEFAULT_SNACKBAR_DURATION } from "../../constants";
import ConfirmDialog from "../shared/confirm-dialog";
import { updatePassword } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { PASSWORD_MAX_LENGTH, Validator } from "helpers/validator";

interface SetUserPasswordDialogProps {
  userId: string;
  userEmail: string;
  regionCode: string;
  open: boolean;
  setOpen: (newOpen: boolean) => void;
}

const SetUserPasswordDialog = ({
  userId,
  userEmail,
  regionCode,
  open,
  setOpen,
}: SetUserPasswordDialogProps) => {
  const [password, setPassword] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const { showError } = useContext(SnackbarContext);

  const validator = useMemo(() => new Validator(), []);
  const errors = validator.validatePassword(password);
  const firstError = errors[0];
  const trimmed = password.trim();
  // While the field is empty, don't surface "Password is required" inline —
  // it's the obvious empty state. Once typing starts, show the first failing rule.
  const shouldShowError = password.length > 0 && firstError !== undefined;
  const canSubmit = errors.length === 0;

  const reset = () => setPassword("");

  // Clear the typed password whenever the dialog closes — handles backdrop
  // click and Escape, which bypass ConfirmDialog's onCancel.
  useEffect(() => {
    if (!open) {
      setPassword("");
    }
  }, [open]);

  const handleSubmit = () => {
    updatePassword(
      { userId, password: trimmed },
      callOptionsHeaders(regionCode, userId)
    )
      .then(() => {
        reset();
        setSuccessOpen(true);
      })
      .catch(showError);
  };

  return (
    <>
      <ConfirmDialog
        title="Set User Password"
        open={open}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Set Password"
        disabled={!canSubmit}
        setOpen={setOpen}
        onConfirm={handleSubmit}
        onCancel={reset}
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          <DialogContentText>
            Set a new password for <strong>{userEmail || userId}</strong>. The
            user can sign in with this password immediately.
          </DialogContentText>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={shouldShowError}
            helperText={
              shouldShowError
                ? firstError
                : "8-50 chars, with upper, lower, and a number or special character"
            }
            fullWidth
            inputProps={{
              "data-testid": "set-user-password-input",
              maxLength: PASSWORD_MAX_LENGTH,
            }}
          />
        </Stack>
      </ConfirmDialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={successOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessOpen(false)}
        data-testid="set-user-password-dialog-success"
      >
        <Alert severity="success" variant="filled">
          Password updated successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default SetUserPasswordDialog;
